import dayjs from "dayjs";

import {
  OrderItemReadDtoPreMediaStatusEnum,
  WorkflowPostponedActivityReadDtoActivityEnum,
} from "@/config/apiClient";
import {
  OrderItemLogActionTypeEnum,
  RetroplanningStatusEnum,
} from "@/config/apiClient.types";
import { isWorkflowWithQuote } from "@/helpers/validators";
import {
  type FormatOrderItemStepsFromHistoryParams,
  findLastLog,
  findLastLogAfter,
  findLastLogAfterDate,
  getRetroplanningDueDate,
  hasNoPostponedActivities,
  type StepConfig,
  spliceActionLog,
} from "@/helpers/workflows";
import { type OrderItemStep } from "@/pages/OrderPage/Order/OrderTracking/OrderTrackingOrderItem";
import { tolgee } from "@/providers/TranslationProvider";

// Formal representation of a orderItem timeline. Allow you to bind an action to it
export const formatOrderItemStepsFromHistory = ({
  orderItem,
  isOMS,
  actions,
  isRetroplanningActive,
}: FormatOrderItemStepsFromHistoryParams): OrderItemStep[] => {
  if (!orderItem) {
    return [];
  }

  const orderItemLogs = spliceActionLog(orderItem.OrderLog);
  const isWithQuote = isWorkflowWithQuote(orderItem.Workflow.workflowType);

  const preMediaPropositionValidatedTitle = isOMS
    ? "orderTracking.step.OMS.propositionValidated"
    : "orderTracking.step.CLIENT.propositionValidated";

  const workflowActivities = new Set(orderItem.Workflow.activities);

  // Retroplanning logs
  const retroplanningSharedAtLog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.RETROPLANNING_SHARED
  );

  // Modification requests logs
  const modificationRequestCreatedlog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.CREATED_MODIFICATION_REQUEST
  );

  const modificationRequestValidatedLog = findLastLogAfter(
    orderItemLogs,
    OrderItemLogActionTypeEnum.ACCEPTED_MODIFICATION_REQUEST,
    modificationRequestCreatedlog,
    false
  );

  const modificationRequestRefusedLog = findLastLogAfter(
    orderItemLogs,
    OrderItemLogActionTypeEnum.REFUSED_MODIFICATION_REQUEST,
    modificationRequestCreatedlog,
    false
  );

  const modificationRequestCancelledLog = findLastLogAfter(
    orderItemLogs,
    OrderItemLogActionTypeEnum.CANCELLED_MODIFICATION_REQUEST,
    modificationRequestCreatedlog,
    false
  );

  // Quote logs
  const quoteRefusedLog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.QUOTE_REFUSED
  );

  const quoteCancelledLog = findLastLogAfter(
    orderItemLogs,
    OrderItemLogActionTypeEnum.QUOTE_CANCELLED,
    modificationRequestValidatedLog
  );

  const quoteAddedLog = findLastLogAfter(
    orderItemLogs,
    OrderItemLogActionTypeEnum.QUOTE_AVAILABLE,
    [quoteRefusedLog, quoteCancelledLog, modificationRequestValidatedLog],
    true,
    () =>
      !orderItem?.Workflow.PostponedActivities.some(
        (activity) =>
          activity.activity ===
          WorkflowPostponedActivityReadDtoActivityEnum.AddQuote
      )
  );

  const quoteSentLog = findLastLogAfter(
    orderItemLogs,
    OrderItemLogActionTypeEnum.QUOTE_AVAILABLE,
    [quoteRefusedLog, quoteCancelledLog, modificationRequestValidatedLog],
    true,
    () =>
      hasNoPostponedActivities(orderItem, [
        WorkflowPostponedActivityReadDtoActivityEnum.SendQuote,
        WorkflowPostponedActivityReadDtoActivityEnum.AddQuote,
      ])
  );

  const quoteValidatedLog = findLastLogAfter(
    orderItemLogs,
    OrderItemLogActionTypeEnum.QUOTE_VALIDATED,
    [quoteCancelledLog, modificationRequestValidatedLog],
    true,
    () =>
      hasNoPostponedActivities(orderItem, [
        WorkflowPostponedActivityReadDtoActivityEnum.SendQuote,
        WorkflowPostponedActivityReadDtoActivityEnum.AddQuote,
      ])
  );

  // Purchase order logs
  const purchaseOrderAddedLog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.ADD_ORDER_NUMBER
  );

  // Delivery label logs
  const deliveryLabelAddedLog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.DELIVERY_LABEL_SUBMITTED
  );

  const deliveryLabelValidatedLog = findLastLogAfter(
    orderItemLogs,
    OrderItemLogActionTypeEnum.DELIVERY_LABEL_VALIDATED,
    [deliveryLabelAddedLog],
    true
  );

  // HDF logs
  const HDFAddedLog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.ADD_GRAPHIC
  );

  // Proof logs
  const proofRefusedLog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.REFUSED_PROOF
  );

  const proofCancelledLog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.PROOF_CANCELLED
  );

  const proofAddedLog = findLastLogAfter(
    orderItemLogs,
    OrderItemLogActionTypeEnum.ADD_PROOF,
    [proofRefusedLog, proofCancelledLog],
    true
  );

  const proofValidatedLog = findLastLogAfter(
    orderItemLogs,
    OrderItemLogActionTypeEnum.CONFIRM_PROOF,
    [proofAddedLog, proofCancelledLog],
    true
  );

  // Pre-media logs
  const preMediaBriefValidatedLog = findLastLogAfterDate(
    orderItemLogs,
    OrderItemLogActionTypeEnum.PRE_MEDIA_ACCEPTED,
    orderItem?.createdAt
  );

  const preMediaBriefRefusedLog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.PRE_MEDIA_REFUSED
  );

  const preMediaPropositionAddedLog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.PRE_MEDIA_PROPOSITION_SUBMITTED
  );

  const preMediaPropositionValidatedLog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.PRE_MEDIA_PROPOSITION_ACCEPTED
  );

  // We have no transition record for inProduction as it's an automated step
  const addDeliveryNoteLog = findLastLog(
    orderItemLogs,
    OrderItemLogActionTypeEnum.DELIVERED
  );

  const isPreMediaBriefValidated =
    Boolean(preMediaBriefValidatedLog) &&
    Boolean(preMediaBriefValidatedLog?.createdAt) &&
    (preMediaBriefRefusedLog === undefined ||
      dayjs(preMediaBriefValidatedLog?.createdAt).isAfter(
        dayjs(preMediaBriefRefusedLog.createdAt)
      ));

  const isPreMediaPropositionAccepted =
    orderItem.preMediaStatus ===
    OrderItemReadDtoPreMediaStatusEnum.PropositionAccepted;

  const isModificationRequestPending =
    modificationRequestCreatedlog &&
    !modificationRequestValidatedLog &&
    !modificationRequestRefusedLog &&
    !modificationRequestCancelledLog;

  const stepDefinitions: StepConfig[] = [
    {
      shouldInclude: true,
      activity: "created",
      occuredAt: orderItem.createdAt,
      isActionEnabled: true,
    },
    {
      shouldInclude:
        isRetroplanningActive && workflowActivities.has("SHARE_RETRO_PLANNING"),
      activity: WorkflowPostponedActivityReadDtoActivityEnum.ShareRetroPlanning,
      occuredAt: retroplanningSharedAtLog?.createdAt,
      isActionEnabled: Boolean(isOMS),
    },
    {
      shouldInclude:
        isRetroplanningActive && workflowActivities.has("CHECK_RETRO_PLANNING"),
      activity: WorkflowPostponedActivityReadDtoActivityEnum.CheckRetroPlanning,
      isActionEnabled: true,
    },
    {
      shouldInclude: workflowActivities.has("ADD_PURCHASE_ORDER"),
      activity: WorkflowPostponedActivityReadDtoActivityEnum.AddPurchaseOrder,
      occuredAt: purchaseOrderAddedLog?.createdAt,
      isActionEnabled: true,
      dueDate: !purchaseOrderAddedLog?.createdAt
        ? getRetroplanningDueDate(
            orderItem,
            RetroplanningStatusEnum.PURCHASE_ORDER_MISSING
          )
        : undefined,
    },
    {
      shouldInclude: workflowActivities.has("ADD_QUOTE") && isWithQuote,
      activity: WorkflowPostponedActivityReadDtoActivityEnum.AddQuote,
      loopBackAction: actions?.CANCEL_QUOTE,
      occuredAt: quoteAddedLog?.createdAt,
      isActionEnabled: Boolean(isOMS),
    },
    {
      shouldInclude: workflowActivities.has("SEND_QUOTE") && isWithQuote,
      activity: WorkflowPostponedActivityReadDtoActivityEnum.SendQuote,
      occuredAt: quoteSentLog?.createdAt,
      isActionEnabled: Boolean(isOMS),
    },
    {
      shouldInclude: workflowActivities.has("DECIDE_ON_QUOTE") && isWithQuote,
      activity: WorkflowPostponedActivityReadDtoActivityEnum.DecideOnQuote,
      occuredAt: quoteValidatedLog?.createdAt,
      isActionEnabled: !isOMS,
      dueDate:
        !isOMS &&
        (!quoteValidatedLog?.createdAt ||
          (quoteValidatedLog &&
            modificationRequestValidatedLog &&
            dayjs(quoteValidatedLog.createdAt).isBefore(
              modificationRequestValidatedLog?.createdAt
            )))
          ? getRetroplanningDueDate(
              orderItem,
              RetroplanningStatusEnum.QUOTE_TO_VALIDATE
            )
          : undefined,
    },
    {
      shouldInclude: workflowActivities.has("ADD_HDF"),
      activity: WorkflowPostponedActivityReadDtoActivityEnum.AddHdf,
      occuredAt: HDFAddedLog?.createdAt,
      isActionEnabled: true,
      dueDate: !HDFAddedLog?.createdAt
        ? getRetroplanningDueDate(
            orderItem,
            RetroplanningStatusEnum.HD_FILE_MISSING
          )
        : undefined,
    },
    {
      shouldInclude: workflowActivities.has("ADD_PROOF"),
      activity: WorkflowPostponedActivityReadDtoActivityEnum.AddProof,
      loopBackAction: actions?.CANCEL_PROOF,
      occuredAt: proofAddedLog?.createdAt,
      isActionEnabled: Boolean(isOMS),
    },
    {
      shouldInclude: workflowActivities.has("DECIDE_ON_PROOF"),
      activity: WorkflowPostponedActivityReadDtoActivityEnum.DecideOnProof,
      occuredAt: proofValidatedLog?.createdAt,
      isActionEnabled: !isOMS,
      dueDate:
        !isOMS && !proofValidatedLog?.createdAt
          ? getRetroplanningDueDate(
              orderItem,
              RetroplanningStatusEnum.PROOF_TO_VALIDATE
            )
          : undefined,
    },
    {
      shouldInclude: workflowActivities.has("ADD_DELIVERY_LABEL"),
      activity: WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryLabel,
      occuredAt: deliveryLabelAddedLog?.createdAt,
      isActionEnabled: Boolean(isOMS),
    },
    {
      shouldInclude: workflowActivities.has("DECIDE_ON_DELIVERY_LABEL"),
      activity:
        WorkflowPostponedActivityReadDtoActivityEnum.DecideOnDeliveryLabel,
      occuredAt: deliveryLabelValidatedLog
        ? deliveryLabelValidatedLog?.createdAt
        : undefined,
      isActionEnabled: !isOMS,
    },
    {
      shouldInclude: workflowActivities.has("ADD_PRE_MEDIA_BRIEF"),
      activity: WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaBrief,
      occuredAt: orderItem.createdAt,
      isActionEnabled: !isOMS,
    },
    {
      shouldInclude: workflowActivities.has("DECIDE_ON_PRE_MEDIA_BRIEF"),
      activity:
        WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaBrief,
      isActionEnabled: Boolean(isOMS),
      occuredAt: isPreMediaBriefValidated
        ? preMediaBriefValidatedLog?.createdAt
        : undefined,
    },
    {
      shouldInclude: workflowActivities.has("ADD_PRE_MEDIA_PROPOSITION"),
      activity:
        WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaProposition,
      isActionEnabled: Boolean(isOMS),
      occuredAt: preMediaPropositionAddedLog?.createdAt,
    },
    {
      shouldInclude: workflowActivities.has("PROCESS_PRE_MEDIA_PROPOSITION"),
      activity:
        WorkflowPostponedActivityReadDtoActivityEnum.ProcessPreMediaProposition,
      isActionEnabled: true,
    },
    {
      shouldInclude: workflowActivities.has("DECIDE_ON_PRE_MEDIA_PROPOSITION"),
      activity:
        WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaProposition,
      title:
        orderItem.preMediaStatus === "PROPOSITION_ACCEPTED"
          ? tolgee.t(preMediaPropositionValidatedTitle, { context: "done" })
          : undefined,
      isActionEnabled: true,
      occuredAt: isPreMediaPropositionAccepted
        ? preMediaPropositionValidatedLog?.createdAt
        : undefined,
    },
    {
      shouldInclude: workflowActivities.has("ADD_DELIVERY_NOTE"),
      activity: WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryNote,
      isActionEnabled: Boolean(isOMS),
      occuredAt: addDeliveryNoteLog?.createdAt,
    },
    {
      shouldInclude: true,
      activity: isModificationRequestPending
        ? "DECIDE_ON_MODIFICATION_REQUEST"
        : WorkflowPostponedActivityReadDtoActivityEnum.CreateModificationRequest,
      isActionEnabled: isModificationRequestPending ? Boolean(isOMS) : !isOMS,
    },
  ];

  return stepDefinitions
    .filter((def) => def.shouldInclude)
    .map((step, index) => {
      const { shouldInclude: _shouldInclude, ...partialStep } = step;

      const activity = partialStep.activity;

      // Logique LoopBack
      const isLoopBackAvailable =
        (activity === WorkflowPostponedActivityReadDtoActivityEnum.AddQuote &&
          orderItem.Workflow.PostponedActivities.some(
            (a) =>
              a.activity ===
              WorkflowPostponedActivityReadDtoActivityEnum.CancelQuote
          )) ||
        (activity === WorkflowPostponedActivityReadDtoActivityEnum.AddProof &&
          orderItem.Workflow.PostponedActivities.some(
            (a) =>
              a.activity ===
              WorkflowPostponedActivityReadDtoActivityEnum.CancelProof
          ));

      // Logique Postponed
      const isPostponedStep =
        orderItem.Workflow.PostponedActivities.some(
          (p) => p.activity === activity
        ) || activity === "DECIDE_ON_MODIFICATION_REQUEST";

      // Logique Occured
      const isOccuredStep =
        activity ===
          WorkflowPostponedActivityReadDtoActivityEnum.CreateModificationRequest ||
        Boolean(partialStep.occuredAt) ||
        (activity === WorkflowPostponedActivityReadDtoActivityEnum.AddQuote &&
          orderItem.Workflow.PostponedActivities.some(
            (p) =>
              p.activity ===
              WorkflowPostponedActivityReadDtoActivityEnum.SendQuote
          )) ||
        (activity ===
          WorkflowPostponedActivityReadDtoActivityEnum.ProcessPreMediaProposition &&
          (orderItem.Workflow.PostponedActivities.some(
            (p) =>
              p.activity ===
              WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaProposition
          ) ||
            orderItem.preMediaStatus ===
              OrderItemReadDtoPreMediaStatusEnum.PropositionAccepted));

      return {
        ...partialStep,
        isLoopBackAvailable,
        isPostponedStep,
        isOccuredStep,
        index,
        action: actions?.[activity],
      };
    });
};
